import React from 'react';
import PropTypes from 'prop-types';

export const Button = ({ children, onClick, variant = 'default', size = 'medium', disabled = false, style = {}, type = 'button', ...props }) => {
  const baseStyles = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '3px',
    padding: size === 'small' ? '6px 12px' : size === 'large' ? '12px 24px' : '8px 16px',
    border: '1px solid #dee2e6',
    borderRadius: '4px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: size === 'small' ? '12px' : size === 'large' ? '16px' : '14px',
    fontWeight: '500',
    transition: 'all 0.2s ease',
    ...style
  };

  const variantStyles = {
    default: {
      backgroundColor: '#f8f9fa',
      color: '#6c757d',
      ':hover': !disabled ? { backgroundColor: '#e9ecef' } : {}
    },
    primary: {
      backgroundColor: '#007bff',
      color: '#fff',
      borderColor: '#007bff',
      ':hover': !disabled ? { backgroundColor: '#0056b3' } : {}
    },
    active: {
      backgroundColor: '#333',
      color: '#fff',
      borderColor: '#333'
    },
    secondary: {
      backgroundColor: 'transparent',
      color: '#6c757d',
      ':hover': !disabled ? { backgroundColor: '#f8f9fa' } : {}
    }
  };

  return (
    <button
      disabled={disabled}
      onClick={disabled ? undefined : onClick}
      style={{
        ...baseStyles,
        ...variantStyles[variant],
        opacity: disabled ? 0.6 : 1
      }}
      type={type}
      {...props}
    >
      {children}
    </button>
  );
};

Button.propTypes = {
  children: PropTypes.node.isRequired,
  onClick: PropTypes.func,
  variant: PropTypes.oneOf(['default', 'primary', 'active', 'secondary']),
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  disabled: PropTypes.bool,
  style: PropTypes.object,
  type: PropTypes.string
};

export const Select = ({ options = [], value, onChange, placeholder = 'Select...', style = {}, ...props }) => {
  const selectStyles = {
    width: '100%',
    padding: '10px',
    border: '1px solid #dee2e6',
    borderRadius: '4px',
    fontSize: '14px',
    backgroundColor: '#fff',
    ...style
  };

  return (
    <select onChange={onChange} style={selectStyles} value={value ?? ''} {...props}>
      <option disabled={true} value=''>
        {placeholder}
      </option>
      {options.map((option, index) => (
        <option key={index} value={option.value || option}>
          {option.label || option}
        </option>
      ))}
    </select>
  );
};

Select.propTypes = {
  onChange: PropTypes.func.isRequired,
  options: PropTypes.array,
  placeholder: PropTypes.string,
  style: PropTypes.object,
  value: PropTypes.string
};

export const Input = ({ type = 'text', value, onChange, placeholder, style = {}, ...props }) => {
  const inputStyles = {
    width: '100%',
    padding: '10px',
    border: '1px solid #dee2e6',
    borderRadius: '4px',
    fontSize: '14px',
    backgroundColor: '#fff',
    boxSizing: 'border-box',
    ...style
  };

  return <input onChange={onChange} placeholder={placeholder} style={inputStyles} type={type} value={value} {...props} />;
};

Input.propTypes = {
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  style: PropTypes.object,
  type: PropTypes.string,
  value: PropTypes.string
};

export const ButtonGroup = ({ children, style = {} }) => {
  const groupStyles = {
    display: 'flex',
    gap: '8px',
    marginBottom: '8px',
    ...style
  };

  return <div style={groupStyles}>{children}</div>;
};

ButtonGroup.propTypes = {
  children: PropTypes.node.isRequired,
  style: PropTypes.object
};

export const Field = ({ label, children, style = {}, required = false }) => {
  const fieldStyles = {
    marginBottom: '24px',
    ...style
  };

  const labelStyles = {
    display: 'block',
    marginBottom: '8px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#333'
  };

  return (
    <div style={fieldStyles}>
      {label && (
        <label style={labelStyles}>
          {label}
          {required && <span style={{ color: 'red', marginLeft: 4 }}>*</span>}
        </label>
      )}
      {children}
    </div>
  );
};

Field.propTypes = {
  children: PropTypes.node.isRequired,
  required: PropTypes.bool,
  label: PropTypes.string,
  style: PropTypes.object
};
